import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";

/**
 * The Discovery Offers filters (spec 10.6): Search, Campaign, Tier, Status, Smart
 * Offers, Expiring Soon.
 *
 * Category was the seventh and was removed in round 4 (G-13, decision D3): it filtered
 * on a field no customer ever sees and nothing sets any more. See OfferEditor for the
 * full reasoning and the scope.
 *
 * Filtering is CLIENT-SIDE and the filter function is a pure export, for two reasons.
 * An account's offers are bounded by construction - Silver 3 and Gold 5 per campaign,
 * plus Discovery Boost - so even a large account lands in the low hundreds and one
 * fetch is cheaper than seven query parameters and a pagination contract. And a pure
 * function is testable without a browser, which is what the rest of this codebase's
 * rules already rely on.
 */

// spec 10.6.7 asks for "Expiring Soon" without defining soon.
//
// This has moved twice. It began as a fixed 14 days, became a selectable window in round
// 2 ("maybe make selectable by week"), and round 4 asked for the middle ground: one
// button, a fixed fortnight decided here, and the remaining days shown on each offer.
//
// The last version is better and it is worth saying why. The dropdown made a merchant
// answer "how soon is soon?" before they could see anything, and the answer was almost
// always the default. Showing "Expiring in 3 days" per offer gives them the same
// information without asking the question.
//
// The plumbing stays parameterised deliberately: isExpiringSoon keeps its `weeks`
// argument and these constants stay exported, so the window can move again without
// hunting for a hard-coded 14.
export const DEFAULT_EXPIRING_WEEKS = 2;

// Kept as the default window in days, for callers that still want one number.
export const EXPIRING_SOON_DAYS = DEFAULT_EXPIRING_WEEKS * 7;

export const SENTINEL_ALL = "__all__";

export const emptyFilters = () => ({
  search: "",
  campaignId: SENTINEL_ALL,
  tier: SENTINEL_ALL,
  status: SENTINEL_ALL,
  smartOnly: false,
  expiringSoon: false,
});

export function isExpiringSoon(offer, now = new Date(), weeks = DEFAULT_EXPIRING_WEEKS) {
  // Only a live campaign can be "expiring". Something already over has expired, not
  // "soon", and surfacing it here would bury the offers a merchant can still act on.
  if (offer.status !== "ACTIVE") return false;
  const end = offer.campaign_end_at ? new Date(offer.campaign_end_at) : null;
  if (!end || Number.isNaN(end.getTime())) return false;
  const days = (end.getTime() - now.getTime()) / 86400000;
  // A nonsense window falls back to the default rather than matching nothing, which
  // would read as "no offers are expiring" - the opposite of the answer.
  const window = Number(weeks) > 0 ? Number(weeks) * 7 : DEFAULT_EXPIRING_WEEKS * 7;
  return days >= 0 && days <= window;
}

/** Days until an offer's campaign ends, or null when there is no usable date.
 *
 * G-17 asks each result to show its own countdown ("Expiring in 3 days") rather than the
 * merchant choosing a window. Rounded UP, so an offer with a few hours left reads
 * "1 day" rather than "0 days", which would look like it had already gone.
 */
export function daysUntilExpiry(offer, now = new Date()) {
  const end = offer?.campaign_end_at ? new Date(offer.campaign_end_at) : null;
  if (!end || Number.isNaN(end.getTime())) return null;
  const days = Math.ceil((end.getTime() - now.getTime()) / 86400000);
  return days >= 0 ? days : null;
}

/** "Expiring in 3 days", or null when there is nothing sensible to say. */
export function expiryLabel(offer, now = new Date()) {
  const days = daysUntilExpiry(offer, now);
  if (days === null) return null;
  if (days === 0) return "Expires today";
  return `Expiring in ${days} ${days === 1 ? "day" : "days"}`;
}

/** Every active filter narrows; an untouched filter set returns everything. */
export function applyOfferFilters(offers, filters, now = new Date()) {
  const f = { ...emptyFilters(), ...(filters || {}) };
  const needle = f.search.trim().toLowerCase();

  return (offers || []).filter((o) => {
    if (needle) {
      const haystack = [
        o.display_value,
        o.campaign_name,
        o.payload?.description,
        o.payload?.item_name,
        o.payload?.conditions,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (f.campaignId !== SENTINEL_ALL && o.campaign_id !== f.campaignId) return false;
    if (f.tier !== SENTINEL_ALL && o.campaign_tier !== f.tier) return false;
    if (f.status !== SENTINEL_ALL && o.status !== f.status) return false;
    if (f.smartOnly && o.offer_type !== "SMART") return false;
    if (f.expiringSoon && !isExpiringSoon(o, now)) return false;
    return true;
  });
}

export function activeFilterCount(filters) {
  const f = { ...emptyFilters(), ...(filters || {}) };
  let n = 0;
  if (f.search.trim()) n++;
  ["campaignId", "tier", "status"].forEach((k) => {
    if (f[k] !== SENTINEL_ALL) n++;
  });
  if (f.smartOnly) n++;
  if (f.expiringSoon) n++;
  return n;
}

const TIERS = ["BRONZE", "SILVER", "GOLD"];

export default function OfferFilterBar({ filters, onChange, campaigns = [] }) {
  const set = (key, value) => onChange({ ...filters, [key]: value });
  const count = activeFilterCount(filters);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search offers"
            className="pl-8 h-9"
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            data-testid="offer-search"
          />
        </div>

        <Select value={filters.campaignId} onValueChange={(v) => set("campaignId", v)}>
          <SelectTrigger className="h-9 w-[170px]"><SelectValue placeholder="Campaign" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={SENTINEL_ALL}>All campaigns</SelectItem>
            {campaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.tier} onValueChange={(v) => set("tier", v)}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={SENTINEL_ALL}>All tiers</SelectItem>
            {TIERS.map((t) => (
              <SelectItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* The Category select was here (G-13). It was already invisible, guarded on
            a table nothing seeds, and is removed rather than left dormant so it cannot
            come back by somebody seeding that table. */}

        <Select value={filters.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={SENTINEL_ALL}>All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={filters.smartOnly ? "default" : "outline"}
          size="sm" className="h-9"
          onClick={() => set("smartOnly", !filters.smartOnly)}
          data-testid="filter-smart"
        >
          Smart Offers
        </Button>

        {/* One control (G-17). The window is decided here rather than asked for, and
            each result carries its own countdown, so the merchant gets the detail
            without having to specify it first. */}
        <Button
          variant={filters.expiringSoon ? "default" : "outline"}
          size="sm" className="h-9"
          onClick={() => set("expiringSoon", !filters.expiringSoon)}
          data-testid="filter-expiring"
        >
          Expiring soon
        </Button>

        {count > 0 && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => onChange(emptyFilters())}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear
          </Button>
        )}
      </div>

      {count > 0 && (
        <Badge variant="secondary" className="text-[11px]">
          {count} filter{count === 1 ? "" : "s"} applied
        </Badge>
      )}
    </div>
  );
}
